import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { FaTruck } from 'react-icons/fa';

import Loading from '../../../../components/Loading';
import PaginationBar from '../../../../components/PaginationBar/PaginationBar';
import ReasonConfirmModal from '../../../../components/ReasonConfirmModal';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { useAllPdvs } from '../../../../hooks/useAllPdvs';
import { useUserPdvs } from '../../../../hooks/useUserPdvs';
import {
   cancelShipment,
   listTransferShipments,
   receiveShipment,
   settleShipmentDivergence,
} from '../../../../services/inventory/transfersApi';
import { notifyApiError } from '../../../../utils/feedback';

import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../../styles/layout/ActionBar';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { DataTable } from '../../../../styles/ui/Table';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { CellLink } from '../../../../styles/ui/Link';
import { CtaButton, SmallButton } from '../../../../styles/ui/Button';

import GuardedAction from '../components/GuardedAction';
import ReceiveModal from '../components/ReceiveModal';
import SettleDivergenceModal from '../components/SettleDivergenceModal';
import StockWarningsBanner from '../components/StockWarningsBanner';
import { ShipmentStatusBadge } from '../components/StatusBadges';
import {
   CANCEL_REASON_MAX,
   CANCEL_REASON_MIN,
   ITEMS_PER_PAGE,
   SHIPMENT_SORTABLE_KEYS,
   SHIPMENT_STATUS,
   SHIPMENT_STATUS_ALL,
   SHIPMENT_STATUS_OPTIONS,
   TRANSFER_PERMISSIONS,
} from '../constants';
import { formatDateTimeBr } from '../utils/format';
import { itemErrorsByProdId } from '../utils/itemErrors';
import { buildPdvAccess } from '../utils/pdvAccess';

// ---------------------------------------------------------------------------
// Remessas / "Em trânsito" (GET /transfer-shipments).
//
// É uma FILA DE TRABALHO do PDV de destino, não uma tela de pesquisa — então
// segue o desvio já ratificado para telas desse tipo (Central de cobranças,
// handoff 0090): carrega no mount com o recorte default (`DISPATCHED`) em vez
// de abrir vazia. O que se preserva do `navegacao.md`: a URL espelha o recorte
// com `replace: true`, e F5/deep-link restauram o contexto exato.
//
// Refatoração visual 2026-09-10: o filtro de situação virou ABAS no header
// escuro (segmentado local, sancionado pelo G.2), com clique aplicando na hora
// — troca de recorte de fila é ação explícita, como o clique de ordenação. A
// aba "Canceladas" foi adicionada sobre o protótipo (decisão D5) para não
// perder o filtro direto que o select oferecia. O filtro de PDV continua no
// cartão de filtros, com Pesquisar.
//
// `status` é SEMPRE enviado — omitir significa `DISPATCHED` no backend, não
// "todos". **Desde o adendo 0099 §4.3 (2026-08-21)** existe o valor explícito
// `ALL`, que é o que a aba "Todas" manda.
//
// **Ordenação habilitada em 2026-08-21**: `SHIPMENT_SORTABLE_KEYS` espelha a
// whitelist do backend (§1.10); default `id desc`, e "Situação" ordena pelo
// ciclo de vida do ENUM (em trânsito → conferida → divergência → cancelada).
// ---------------------------------------------------------------------------

const DEFAULT_STATUS = SHIPMENT_STATUS.DISPATCHED;

// Abas de situação — uma por status + "Todas" (D5). Os rótulos curtos são das
// abas; os longos continuam em SHIPMENT_STATUS_OPTIONS (usados na validação).
const STATUS_TABS = [
   { value: SHIPMENT_STATUS.DISPATCHED, label: 'Em trânsito' },
   { value: SHIPMENT_STATUS.RECEIVED, label: 'Conferidas' },
   { value: SHIPMENT_STATUS.RECEIVED_WITH_DIVERGENCE, label: 'Com divergência' },
   { value: SHIPMENT_STATUS.CANCELLED, label: 'Canceladas' },
   { value: SHIPMENT_STATUS_ALL, label: 'Todas' },
];

// Segmentado de header escuro — controle LOCAL por decisão do FC7 §G.2 (não é
// variante do kit). G.4.1: cada estado declara o próprio background no hover.
const StatusTabsWrap = styled.div`
   display: inline-flex;
   gap: 3px;
   padding: 4px;
   border-radius: 11px;
   background: rgba(255, 255, 255, 0.07);
   flex-wrap: wrap;
`;

const StatusTab = styled.button`
   appearance: none;
   border: 0;
   cursor: pointer;
   padding: 8px 13px;
   border-radius: 8px;
   font-size: 12.5px;
   font-weight: 700;
   font-family: inherit;
   background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
   color: ${({ $active, theme }) =>
      $active ? theme.colors.textPrimary : theme.colors.textMutedOnAppBackground};
   transition:
      background 0.13s ease,
      color 0.13s ease;

   &:hover:not(:disabled) {
      background: ${({ $active }) =>
         $active ? '#fff' : 'rgba(255, 255, 255, 0.1)'};
      color: ${({ $active, theme }) =>
         $active ? theme.colors.textPrimary : '#fff'};
   }
   &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.focusRing};
   }
   &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
   }
`;

function readStatusFromUrl(searchParams) {
   const raw = searchParams.get('status');
   const isKnown = SHIPMENT_STATUS_OPTIONS.some(
      (option) => option.value === raw
   );
   return isKnown ? raw : DEFAULT_STATUS;
}

function readSortFromUrl(searchParams) {
   const orderBy = searchParams.get('order_by');
   if (orderBy && SHIPMENT_SORTABLE_KEYS.has(orderBy)) {
      return {
         key: orderBy,
         direction: searchParams.get('order_dir') === 'desc' ? 'desc' : 'asc',
      };
   }
   return { key: null, direction: null };
}

export default function TransferShipmentList() {
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();

   const canReceive = hasPermission(TRANSFER_PERMISSIONS.RECEIVE);
   const canCancel = hasPermission(TRANSFER_PERMISSIONS.CANCEL);
   const canSettle = hasPermission(TRANSFER_PERMISSIONS.SETTLE);

   const { pdvs } = useAllPdvs();
   // Escopo do usuário (≠ catálogo do filtro acima): alimenta a barreira visual
   // das ações. Fail-open enquanto não carregar com sucesso.
   const {
      pdvs: userPdvs,
      isLoading: loadingUserPdvs,
      error: userPdvsError,
   } = useUserPdvs();

   const [searchParams, setSearchParams] = useSearchParams();

   // A aba de situação aplica na hora; o PDV segue rascunho × aplicado.
   const [status, setStatus] = useState(() => readStatusFromUrl(searchParams));
   const [draftPdvId, setDraftPdvId] = useState(
      () => searchParams.get('pdv_id') ?? ''
   );
   const [appliedPdvId, setAppliedPdvId] = useState(draftPdvId);
   const [sortConfig, setSortConfig] = useState(() =>
      readSortFromUrl(searchParams)
   );
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });

   const [shipments, setShipments] = useState([]);
   const [totalPages, setTotalPages] = useState(1);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);

   const [receiveTarget, setReceiveTarget] = useState(null);
   // 422 estruturado por item (handoff 0107 §5.3) — o modal corrige a
   // referência de expedido com o valor atual do servidor.
   const [receiveItemErrors, setReceiveItemErrors] = useState({});
   const [settleTarget, setSettleTarget] = useState(null);
   const [cancelTarget, setCancelTarget] = useState(null);
   const [warnings, setWarnings] = useState(null);

   const didInitialFetch = useRef(false);

   const commitToUrl = useCallback(
      (nextStatus, pdvId, sort, page) => {
         const params = {};
         // O status default fica fora da URL (URL limpa = recorte padrão).
         if (nextStatus && nextStatus !== DEFAULT_STATUS)
            params.status = nextStatus;
         if (pdvId) params.pdv_id = pdvId;
         if (sort.key) {
            params.order_by = sort.key;
            params.order_dir = sort.direction;
         }
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams]
   );

   const fetchShipments = useCallback(async (nextStatus, pdvId, sort, page) => {
      setIsLoading(true);
      try {
         const result = await listTransferShipments({
            page,
            perPage: ITEMS_PER_PAGE,
            status: nextStatus,
            pdvId,
            orderBy: sort.key ?? undefined,
            orderDir: sort.key ? sort.direction : undefined,
         });
         setShipments(result.shipments);
         setTotalPages(result.totalPages);
         setTotalCount(result.total);
         setCurrentPage(result.page);
      } catch (err) {
         notifyApiError(err, { fallback: 'Erro ao buscar remessas.' });
      } finally {
         setIsLoading(false);
      }
   }, []);

   // Fila de trabalho: carrega no mount, com o recorte da URL ou o default.
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      fetchShipments(status, appliedPdvId, sortConfig, currentPage);
   }, [fetchShipments, status, appliedPdvId, sortConfig, currentPage]);

   const refreshCurrent = () =>
      fetchShipments(status, appliedPdvId, sortConfig, currentPage);

   // 409 = o estado mudou por outra mão (handoff 0107) — ressincroniza a fila.
   const syncIfConflict = (err) => {
      if (err?.kind === 'conflict') refreshCurrent();
   };

   // Troca de aba = nova busca por filtro → zera a ordenação e volta à pág. 1.
   const handleStatusTab = (nextStatus) => {
      if (nextStatus === status) return;
      const resetSort = { key: null, direction: null };
      setStatus(nextStatus);
      setSortConfig(resetSort);
      commitToUrl(nextStatus, appliedPdvId, resetSort, 1);
      fetchShipments(nextStatus, appliedPdvId, resetSort, 1);
   };

   const handleSearch = (event) => {
      event.preventDefault();
      const resetSort = { key: null, direction: null };
      setAppliedPdvId(draftPdvId);
      setSortConfig(resetSort);
      commitToUrl(status, draftPdvId, resetSort, 1);
      fetchShipments(status, draftPdvId, resetSort, 1);
   };

   const handleSort = (key) => {
      if (!SHIPMENT_SORTABLE_KEYS.has(key)) return;

      let nextSort;
      if (sortConfig.key === key) {
         nextSort =
            sortConfig.direction === 'asc'
               ? { key, direction: 'desc' }
               : { key: null, direction: null };
      } else {
         nextSort = { key, direction: 'asc' };
      }

      setSortConfig(nextSort);
      commitToUrl(status, appliedPdvId, nextSort, 1);
      fetchShipments(status, appliedPdvId, nextSort, 1);
   };

   const getSortIcon = (key) => {
      if (sortConfig.key !== key) return ' ⇅';
      return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
   };

   const sortableThProps = (key, title) => ({
      className: `sortable${sortConfig.key === key ? ' sort-active' : ''}`,
      onClick: () => handleSort(key),
      title,
   });

   const handlePageChange = (page) => {
      commitToUrl(status, appliedPdvId, sortConfig, page);
      fetchShipments(status, appliedPdvId, sortConfig, page);
   };

   const handleReceive = async (payload) => {
      setIsSubmitting(true);
      setReceiveItemErrors({});
      try {
         const result = await receiveShipment(receiveTarget.id, payload);
         toast.success(result.message);
         setReceiveTarget(null);
         if (result.stockWarnings.length > 0) {
            setWarnings({
               stockWarnings: result.stockWarnings,
               subtitle: `remessa #${result.shipment?.id ?? ''} conferida`,
            });
         }
         refreshCurrent();
      } catch (err) {
         setReceiveItemErrors(itemErrorsByProdId(err));
         notifyApiError(err, {
            fallback: 'Não foi possível registrar a conferência.',
         });
         syncIfConflict(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleSettle = async (payload) => {
      setIsSubmitting(true);
      try {
         const result = await settleShipmentDivergence(settleTarget.id, payload);
         toast.success(result.message);
         setSettleTarget(null);
         refreshCurrent();
      } catch (err) {
         notifyApiError(err, {
            fallback: 'Não foi possível registrar a tratativa.',
         });
         syncIfConflict(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   const handleCancel = async (reason) => {
      setIsSubmitting(true);
      try {
         const result = await cancelShipment(cancelTarget.id, {
            cancelledReason: reason,
         });
         toast.success(result.message);
         setCancelTarget(null);
         refreshCurrent();
      } catch (err) {
         notifyApiError(err, {
            fallback: 'Não foi possível cancelar a remessa.',
         });
         syncIfConflict(err);
      } finally {
         setIsSubmitting(false);
      }
   };

   // Cada linha tem sua própria rota origem→destino — o recorte é por remessa,
   // não por tela (diferente do detalhe da ordem, onde todas compartilham).
   const accessOf = (shipment) =>
      buildPdvAccess({
         userPdvs,
         isReady: !loadingUserPdvs && !userPdvsError,
         entity: shipment,
      });

   const hasPendingDivergence = (shipment) =>
      (shipment.items ?? []).some(
         (item) =>
            Number(item.divergent_qtd) > 0 && !item.divergence_settled_at
      );

   const hasResults = shipments.length > 0;

   // Estado vazio com cópia específica para o recorte padrão da fila.
   const emptyCopy =
      status === SHIPMENT_STATUS.DISPATCHED
         ? {
              title: 'Nada em trânsito',
              description:
                 'Todas as remessas foram conferidas. Troque de aba para revisar o histórico.',
           }
         : {
              title: 'Nenhuma remessa nesta situação',
              description: 'Troque a aba ou o PDV e pesquise novamente.',
           };

   return (
      <Container>
         <Loading isLoading={isLoading || isSubmitting} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Remessas de transferência</PageTitle>
               <PageSubtitle>
                  {totalCount
                     ? `${totalCount} remessa(s) no filtro atual`
                     : 'Fila de conferência do destino — o estoque entra pelo que for contado'}
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <StatusTabsWrap role="tablist" aria-label="Situação da remessa">
                  {STATUS_TABS.map((tab) => (
                     <StatusTab
                        key={tab.value}
                        type="button"
                        role="tab"
                        aria-selected={status === tab.value}
                        $active={status === tab.value}
                        disabled={isLoading}
                        onClick={() => handleStatusTab(tab.value)}
                     >
                        {tab.label}
                     </StatusTab>
                  ))}
               </StatusTabsWrap>
            </PageHeaderActions>
         </PageHeader>

         {warnings && (
            <StockWarningsBanner
               subtitle={warnings.subtitle}
               stockWarnings={warnings.stockWarnings}
               onDismiss={() => setWarnings(null)}
            />
         )}

         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-4">
                        <label htmlFor="shipment-pdv">
                           PDV (origem ou destino)
                        </label>
                        <select
                           id="shipment-pdv"
                           value={draftPdvId}
                           onChange={(event) => setDraftPdvId(event.target.value)}
                        >
                           <option value="">Todos os meus PDVs</option>
                           {pdvs.map((pdv) => (
                              <option key={pdv.id} value={pdv.id}>
                                 {pdv.pdv_name ?? pdv.name ?? `PDV ${pdv.id}`}
                              </option>
                           ))}
                        </select>
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <CtaButton type="submit" disabled={isLoading}>
                           Pesquisar
                        </CtaButton>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         <Section>
            <SectionHeader>
               <SectionTitle>
                  Remessas{totalCount ? ` (${totalCount})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {hasResults ? (
                  <>
                     <TableWrapper $minWidth="1080px">
                        <DataTable>
                           {/* Clicáveis = SHIPMENT_SORTABLE_KEYS. "Conferida
                               em" ganhou coluna própria por estar na whitelist
                               e ser o que o destino procura ao revisar. */}
                           <thead>
                              <tr>
                                 <th
                                    className={`center sortable${sortConfig.key === 'id' ? ' sort-active' : ''}`}
                                    onClick={() => handleSort('id')}
                                    title="Ordenar por número da remessa"
                                 >
                                    #{getSortIcon('id')}
                                 </th>
                                 <th>Origem</th>
                                 <th>Destino</th>
                                 <th className="center">Transferência</th>
                                 <th>Responsável</th>
                                 <th
                                    {...sortableThProps(
                                       'dispatched_at',
                                       'Ordenar por data de expedição'
                                    )}
                                 >
                                    Expedida em{getSortIcon('dispatched_at')}
                                 </th>
                                 <th
                                    {...sortableThProps(
                                       'received_at',
                                       'Ordenar por data de conferência'
                                    )}
                                 >
                                    Conferida em{getSortIcon('received_at')}
                                 </th>
                                 <th className="num">Itens</th>
                                 <th
                                    className={`center sortable${sortConfig.key === 'status' ? ' sort-active' : ''}`}
                                    onClick={() => handleSort('status')}
                                    title="Ordena pelo estágio do ciclo de vida (em trânsito → conferida → divergência → cancelada)"
                                 >
                                    Situação{getSortIcon('status')}
                                 </th>
                                 <th className="center">Ações</th>
                              </tr>
                           </thead>
                           <tbody>
                              {shipments.map((shipment) => {
                                 const isDispatched =
                                    shipment.status ===
                                    SHIPMENT_STATUS.DISPATCHED;
                                 const access = accessOf(shipment);
                                 return (
                                    <tr key={shipment.id}>
                                       <td className="center">
                                          #{shipment.id}
                                       </td>
                                       <td>{shipment.origin_pdv_name ?? '—'}</td>
                                       <td>{shipment.dest_pdv_name ?? '—'}</td>
                                       <td className="center">
                                          <CellLink
                                             to={`/inventory/transfers/${shipment.transfer_order_id}`}
                                          >
                                             #{shipment.transfer_order_id}
                                          </CellLink>
                                       </td>
                                       <td>
                                          {shipment.responsible_employee_name ??
                                             '—'}
                                       </td>
                                       <td className="nowrap">
                                          {formatDateTimeBr(
                                             shipment.dispatched_at
                                          )}
                                       </td>
                                       <td className="nowrap">
                                          {formatDateTimeBr(
                                             shipment.received_at
                                          )}
                                       </td>
                                       <td className="num">
                                          {shipment.items?.length ?? '—'}
                                       </td>
                                       <td className="center">
                                          <ShipmentStatusBadge
                                             value={shipment.status}
                                          />
                                       </td>
                                       <td className="center nowrap">
                                          {isDispatched && canReceive && (
                                             <GuardedAction
                                                reason={access.blockedForDest}
                                             >
                                                <SmallButton
                                                   type="button"
                                                   $intent="write"
                                                   onClick={() =>
                                                      setReceiveTarget(shipment)
                                                   }
                                                >
                                                   Conferir
                                                </SmallButton>
                                             </GuardedAction>
                                          )}
                                          {isDispatched && canCancel && (
                                             <GuardedAction
                                                reason={access.blockedForOrigin}
                                             >
                                                <SmallButton
                                                   type="button"
                                                   $intent="danger"
                                                   onClick={() =>
                                                      setCancelTarget(shipment)
                                                   }
                                                >
                                                   Cancelar
                                                </SmallButton>
                                             </GuardedAction>
                                          )}
                                          {hasPendingDivergence(shipment) &&
                                             canSettle && (
                                                <GuardedAction
                                                   reason={
                                                      access.blockedForEither
                                                   }
                                                >
                                                   <SmallButton
                                                      type="button"
                                                      $intent="write"
                                                      onClick={() =>
                                                         setSettleTarget(
                                                            shipment
                                                         )
                                                      }
                                                   >
                                                      Tratativa
                                                   </SmallButton>
                                                </GuardedAction>
                                             )}
                                          {!isDispatched &&
                                             !hasPendingDivergence(shipment) && (
                                                <SmallButton
                                                   type="button"
                                                   onClick={() =>
                                                      navigate(
                                                         `/inventory/transfers/${shipment.transfer_order_id}`
                                                      )
                                                   }
                                                >
                                                   Ver ordem
                                                </SmallButton>
                                             )}
                                       </td>
                                    </tr>
                                 );
                              })}
                           </tbody>
                        </DataTable>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               ) : (
                  <EmptyState
                     icon={<FaTruck size={20} />}
                     title={emptyCopy.title}
                     description={emptyCopy.description}
                  />
               )}
            </SectionBody>
         </Section>

         <ReceiveModal
            isOpen={!!receiveTarget}
            shipment={receiveTarget}
            isSubmitting={isSubmitting}
            itemErrors={receiveItemErrors}
            onCancel={() => {
               setReceiveTarget(null);
               setReceiveItemErrors({});
            }}
            onConfirm={handleReceive}
         />

         <SettleDivergenceModal
            isOpen={!!settleTarget}
            shipment={settleTarget}
            isSubmitting={isSubmitting}
            onCancel={() => setSettleTarget(null)}
            onConfirm={handleSettle}
         />

         <ReasonConfirmModal
            isOpen={!!cancelTarget}
            title="Cancelar remessa em trânsito"
            subtitle={cancelTarget ? `Remessa #${cancelTarget.id}` : null}
            infoText="O estoque desta remessa volta para o PDV de origem por um movimento de reversão."
            infoTone="danger"
            reasonLabel="Motivo do cancelamento"
            placeholder="Descreva o motivo (mín. 8 caracteres)"
            minLength={CANCEL_REASON_MIN}
            maxLength={CANCEL_REASON_MAX}
            confirmLabel="Cancelar remessa"
            submittingLabel="Cancelando…"
            cancelLabel="Voltar"
            isSubmitting={isSubmitting}
            inputId="shipment-list-cancel-reason"
            onCancel={() => setCancelTarget(null)}
            onConfirm={handleCancel}
         />
      </Container>
   );
}
